import React from 'react';
import { Link } from 'react-router-dom';
import { CircleCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Panel } from 'components/ui/panel';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import EmptyState from 'components/ui/EmptyState';
import { StatusBadge } from 'components/ui/status-badge';
import { cn } from 'lib/utils';
import type { PendingTestGroup } from '../analyticsFormat';

/** Tests listed one by one; the rest of the grading queue folds into one row. */
const MAX_TEST_ROWS = 3;

interface AttentionRow {
  key: string;
  tone: 'warning' | 'error';
  title: string;
  meta?: string;
  actionLabel: string;
  /** A route, or a handler when the target is a view on this page. */
  to?: string;
  onAction?: () => void;
}

interface Props {
  /** Pending manual grading, per test (from /grading/pending). */
  pendingGroups: PendingTestGroup[];
  /** Integrity events in the window; 0 when the plan has no integrity analytics. */
  integrityEvents: number;
  onReviewIntegrity: () => void;
  isLoading: boolean;
  className?: string;
}

const DOT: Record<AttentionRow['tone'], string> = {
  warning: 'bg-warning-500',
  error: 'bg-error-600',
};

/** Things waiting on the team, from real signals only: grading per test, then integrity. */
export const NeedsAttentionQueue: React.FC<Props> = ({
  pendingGroups,
  integrityEvents,
  onReviewIntegrity,
  isLoading,
  className,
}) => {
  const { t } = useTranslation();

  const listed = pendingGroups.slice(0, MAX_TEST_ROWS);
  const folded = pendingGroups.slice(MAX_TEST_ROWS);
  const rows: AttentionRow[] = listed.map((group) => ({
    key: `grading-${group.testId}`,
    tone: 'warning',
    title: t('na_grading', { count: group.answers }),
    meta: group.title || undefined,
    actionLabel: t('grade'),
    to: '/app/grading',
  }));
  if (folded.length > 0) {
    rows.push({
      key: 'grading-more',
      tone: 'warning',
      title: t('na_grading', { count: folded.reduce((sum, group) => sum + group.answers, 0) }),
      meta: t('analytics_attention_more_tests', { count: folded.length }),
      actionLabel: t('grade'),
      to: '/app/grading',
    });
  }
  if (integrityEvents > 0) {
    rows.push({
      key: 'integrity',
      tone: 'error',
      title: t('na_integrity', { count: integrityEvents }),
      actionLabel: t('review'),
      onAction: onReviewIntegrity,
    });
  }

  return (
    <Panel
      title={t('needs_attention')}
      aside={
        rows.length > 0 ? (
          <StatusBadge tone="warning" dot={false} className="tabular-nums">
            {rows.length}
          </StatusBadge>
        ) : undefined
      }
      flush
      className={className}
    >
      {isLoading ? (
        <div className="divide-y divide-border">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3">
              <Skeleton className="h-2 w-2 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className={cn('h-3.5', i % 2 ? 'w-1/2' : 'w-2/3')} />
                <Skeleton className="h-3 w-1/3" />
              </div>
              <Skeleton className="h-8 w-20" />
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState icon={<CircleCheck />} title={t('na_all_caught_up')} className="py-10" />
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((row, index) => {
            // One lime action: grading at the top of the queue; every other row stays secondary.
            const variant = index === 0 && row.tone === 'warning' ? 'default' : 'outline';
            return (
              <li key={row.key} data-attention-row={row.key} className="flex items-center gap-3 px-4 py-3">
                <span aria-hidden className={cn('h-2 w-2 shrink-0 rounded-full', DOT[row.tone])} />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-[13.5px] font-medium text-foreground">{row.title}</span>
                  {row.meta && <span className="truncate text-[12.5px] text-muted-foreground">{row.meta}</span>}
                </span>
                {row.to ? (
                  <Button asChild size="xs" variant={variant} className="shrink-0">
                    <Link to={row.to}>{row.actionLabel}</Link>
                  </Button>
                ) : (
                  <Button size="xs" variant={variant} className="shrink-0" onClick={row.onAction}>
                    {row.actionLabel}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
};

export default NeedsAttentionQueue;
